<script lang="ts" setup>
import { Form, FormField, type FormInstance } from '@primevue/forms';
import { useRouter } from 'vue-router';
import { AppButton, AppPassword, AppTextInput } from '~/components/base';
import useLogin from '~/composables/auth/useLogin';
import LoginForm from '~/utils/contents/auth/forms/LoginForm';

const router = useRouter();
const route = useRoute();
const formRef = useTemplateRef<FormInstance>('forms');

const { loading, form, errors, resolver, validations, login } = useLogin({
  initialForm: {
    email: route.query.email ? String(route.query.email) : undefined,
    invite_code: route.query.invite_code
      ? String(route.query.invite_code)
      : undefined,
  },
});

const isFormInvalid = computed(
  () => !validations.safeParse(form.value).success,
);

const onFormSubmit = async ({ valid }: { valid: boolean }): Promise<void> => {
  if (!valid) return;

  const result = await login();
  if (result) {
    router.push('/dashboard');
  }
};

const hasInviteCode = computed((): boolean => {
  return !!form.value.invite_code;
});

onMounted(() => {
  if (hasInviteCode.value) {
    formRef.value?.setFieldValue('email', form.value.email || '');
  }
});
</script>

<template>
  <Form
    ref="forms"
    :resolver="resolver"
    class="login-form flex w-full flex-col"
    data-testid="login-form"
    @submit="onFormSubmit"
  >
    <FormField v-slot="$field" name="email" class="mb-3">
      <AppTextInput
        v-model="form.email"
        required
        :label="LoginForm.fields.email.label"
        :placeholder="LoginForm.fields.email.placeholder"
        :data-testid="LoginForm.fields.email.dataTestId"
        :disabled="loading || hasInviteCode"
        :invalid="$field?.invalid || !!errors.email?.length"
        :error-message="$field.error?.message || errors.email?.[0]"
        autocomplete="email"
        class="w-full"
      />
    </FormField>
    <FormField v-slot="$field" name="password" class="mb-3">
      <AppPassword
        v-model="form.password"
        required
        :label="LoginForm.fields.password.label"
        :placeholder="LoginForm.fields.password.placeholder"
        :data-testid="LoginForm.fields.password.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid || !!errors.password?.length"
        :error-message="$field.error?.message || errors.password?.[0]"
        autocomplete="password"
        :feedback="false"
        class="w-full"
      />
    </FormField>

    <NuxtLink
      to="/auth/forgot-password"
      class="login-form__forgot-password text-color-brand my-8 font-bold"
      data-testid="forgot-password-link"
      >Forgot Password</NuxtLink
    >
    <AppButton
      type="submit"
      :data-testid="LoginForm.actions.submitButton.dataTestId"
      :disabled="loading || isFormInvalid"
      :loading="loading"
      :label="LoginForm.actions.submitButton.label"
    />
  </Form>
</template>

<style lang="scss" scoped>
.login-form {
}
</style>
